<template>
  <div class="monthly-comparison">
    
    <!-- 数据概览 -->
    <el-card class="data-card overview-card" v-loading="loading">
      <div slot="header" class="card-header">
        <span>数据概览 ({{ currentMonthLabel }})</span>
      </div>
      <div class="overview-content">
        <div class="overview-row">
          <div class="overview-col">
            <div class="stat-item">
              <div class="stat-label">场次</div>
              <div class="stat-value">{{ latestStats.gameCount || 0 }}</div>
              <div class="stat-trend" :class="getTrendClass(latestStats.gameCountTrend)">
                {{ formatTrend(latestStats.gameCountTrend) }}
              </div>
            </div>
          </div>
          <div class="overview-col">
            <div class="stat-item">
              <div class="stat-label">得分率</div>
              <div class="stat-value">{{ formatPercent(latestStats.scoreRate) }}</div>
              <div class="stat-trend" :class="getTrendClass(latestStats.scoreRateTrend)">
                {{ formatTrend(latestStats.scoreRateTrend) }}
              </div>
            </div>
          </div>
          <div class="overview-col">
            <div class="stat-item">
              <div class="stat-label">订正率</div>
              <div class="stat-value">{{ formatPercent(latestStats.fixRate) }}</div>
              <div class="stat-trend" :class="getTrendClass(latestStats.fixRateTrend)">
                {{ formatTrend(latestStats.fixRateTrend) }}
              </div>
            </div>
          </div>
          <div class="overview-col">
            <div class="stat-item">
              <div class="stat-label">复盘率</div>
              <div class="stat-value">{{ formatPercent(latestStats.reviewRate) }}</div>
              <div class="stat-trend" :class="getTrendClass(latestStats.reviewRateTrend)">
                {{ formatTrend(latestStats.reviewRateTrend) }}
              </div>
            </div>
          </div>
          <div class="overview-col">
            <div class="stat-item">
              <div class="stat-label">掌握率</div>
              <div class="stat-value">{{ formatPercent(latestStats.masteryRate) }}</div>
              <div class="stat-trend" :class="getTrendClass(latestStats.masteryRateTrend)">
                {{ formatTrend(latestStats.masteryRateTrend) }}
              </div>
            </div>
          </div>
          <div class="overview-col">
            <div class="stat-item">
              <div class="stat-label">排位率</div>
              <div class="stat-value">Top {{ formatPercent(latestStats.rankRate) }}</div>
              <div class="stat-trend" :class="getTrendClass(-latestStats.rankRateTrend)">
                {{ formatTrend(-latestStats.rankRateTrend) }}
              </div>
            </div>
          </div>
          <div class="overview-col">
            <div class="stat-item">
              <div class="stat-label">
                平均难度
                <el-tooltip effect="dark" placement="top" popper-class="difficulty-tooltip">
                  <div slot="content" class="difficulty-tooltip-content">
                    <div class="difficulty-levels">
                      <div class="difficulty-level">
                        <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#F56C6C;"></span>
                        <span class="level-range">00.00 ~ 14.29%:</span>
                        <span class="level-name">红题（很简单）</span>
                      </div>
                      <div class="difficulty-level">
                        <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#E6A23C;"></span>
                        <span class="level-range">14.29 ~ 28.58%:</span>
                        <span class="level-name">橙题（简单）</span>
                      </div>
                      <div class="difficulty-level">
                        <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#F7BA2A;"></span>
                        <span class="level-range">28.58 ~ 42.87%:</span>
                        <span class="level-name">黄题（较易）</span>
                      </div>
                      <div class="difficulty-level">
                        <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#67C23A;"></span>
                        <span class="level-range">42.87 ~ 57.15%:</span>
                        <span class="level-name">绿题（中等）</span>
                      </div>
                      <div class="difficulty-level">
                        <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#409EFF;"></span>
                        <span class="level-range">57.15 ~ 71.44%:</span>
                        <span class="level-name">蓝题（较难）</span>
                      </div>
                      <div class="difficulty-level">
                        <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#8E44AD;"></span>
                        <span class="level-range">71.44 ~ 85.72%:</span>
                        <span class="level-name">紫题（很难）</span>
                      </div>
                      <div class="difficulty-level">
                        <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#2C3E50;"></span>
                        <span class="level-range">85.72 ~ 100%:</span>
                        <span class="level-name">黑题（极难）</span>
                      </div>
                    </div>
                  </div>
                  <i class="el-icon-question" style="cursor: pointer; margin-left: 4px; font-size: 14px;"></i>
                </el-tooltip>
              </div>
              <div class="stat-value">
                {{ formatPercent(latestStats.avgDifficulty) }}
              </div>
              <div class="stat-trend" :class="getTrendClass(latestStats.avgDifficultyTrend)">
                {{ formatTrend(latestStats.avgDifficultyTrend) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-card>
    
    <!-- 月度数据表格 -->
    <el-card class="data-card table-card" v-loading="loading">
      <div slot="header" class="card-header">
        <span>月度数据表格</span>
        <div class="header-actions">
          <el-radio-group v-model="timeRange" size="small" @change="handleTimeRangeChange">
            <el-radio-button label="recent">最近一年</el-radio-button>
            <el-radio-button label="all">全部数据</el-radio-button>
          </el-radio-group>
        </div>
      </div>
      <div class="table-wrapper">
        <el-table
          :data="tableData"
          border
          stripe
          style="width: 100%"
          :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
          :min-width="tableColumns.length * 100 + 200"
        >
          <el-table-column prop="indicator" label="指标" width="100" fixed="left">
            <template slot="header" slot-scope="scope">
              <span>指标</span>
            </template>
            <template slot-scope="scope">
              <div v-if="scope.row.type === 'avgDifficulty'">
                <span>平均难度</span>
                <el-tooltip effect="dark" placement="top" popper-class="difficulty-tooltip">
                  <div slot="content" class="difficulty-tooltip-content">
                    <div class="difficulty-levels">
                      <div class="difficulty-level">
                        <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#F56C6C;"></span>
                        <span class="level-range">00.00 ~ 14.29%:</span>
                        <span class="level-name">红题（很简单）</span>
                      </div>
                      <div class="difficulty-level">
                        <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#E6A23C;"></span>
                        <span class="level-range">14.29 ~ 28.58%:</span>
                        <span class="level-name">橙题（简单）</span>
                      </div>
                      <div class="difficulty-level">
                        <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#F7BA2A;"></span>
                        <span class="level-range">28.58 ~ 42.87%:</span>
                        <span class="level-name">黄题（较易）</span>
                      </div>
                      <div class="difficulty-level">
                        <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#67C23A;"></span>
                        <span class="level-range">42.87 ~ 57.15%:</span>
                        <span class="level-name">绿题（中等）</span>
                      </div>
                      <div class="difficulty-level">
                        <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#409EFF;"></span>
                        <span class="level-range">57.15 ~ 71.44%:</span>
                        <span class="level-name">蓝题（较难）</span>
                      </div>
                      <div class="difficulty-level">
                        <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#8E44AD;"></span>
                        <span class="level-range">71.44 ~ 85.72%:</span>
                        <span class="level-name">紫题（很难）</span>
                      </div>
                      <div class="difficulty-level">
                        <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#2C3E50;"></span>
                        <span class="level-range">85.72 ~ 100%:</span>
                        <span class="level-name">黑题（极难）</span>
                      </div>
                    </div>
                  </div>
                  <i class="el-icon-question" style="cursor: pointer; margin-left: 4px; font-size: 14px;"></i>
                </el-tooltip>
              </div>
              <span v-else>{{ scope.row.indicator }}</span>
            </template>
          </el-table-column>
          <el-table-column
            v-for="(column, index) in tableColumns"
            :key="column"
            :label="column"
            align="center"
            min-width="100"
          >
            <template slot-scope="scope">
              <div v-if="scope.row.type === 'avgDifficulty' && scope.row[`value${index}`] !== '-'">
                <el-tag
                  :style="{ 
                    backgroundColor: getDifficultyColorByPercentage(extractNumericValue(scope.row[`value${index}`]) * 100),
                    borderColor: getDifficultyColorByPercentage(extractNumericValue(scope.row[`value${index}`]) * 100),
                    color: 'white'
                  }"
                  :title="getDifficultyLevelByPercentage(extractNumericValue(scope.row[`value${index}`]) * 100)"
                  size="small">
                  <span style="font-weight: bold">{{ formatPercentWithoutHTML(scope.row[`value${index}`]) }}</span>
                </el-tag>
              </div>
              <div v-else-if="scope.row[`value${index}`] !== '-'" v-html="scope.row[`value${index}`]"></div>
              <div v-if="scope.row[`trend${index}`] !== null" class="trend-indicator" :class="getTrendClass(scope.row[`trend${index}`])">
                {{ formatTrendArrow(scope.row[`trend${index}`]) }}
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>
    
    <!-- 数据可视化 -->
    <el-card class="data-card chart-card" v-loading="loading">
      <div slot="header" class="card-header">
        <span>指标综合分析</span>
        <div class="header-actions">
          <el-radio-group v-model="timeRange" size="small" @change="handleTimeRangeChange">
            <el-radio-button label="recent">最近一年</el-radio-button>
            <el-radio-button label="all">全部数据</el-radio-button>
          </el-radio-group>
        </div>
      </div>
      <div class="chart-container" ref="chartContainer"></div>
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'

export default {
  name: 'MonthlyComparison',
  data() {
    return {
      loading: false,
      timeRange: 'recent', // 默认显示最近一年的数据
      currentMonthLabel: '',
      latestStats: {
        gameCount: 0,
        gameCountTrend: 0,
        scoreRate: 0,
        scoreRateTrend: 0,
        fixRate: 0,
        fixRateTrend: 0,
        reviewRate: 0,
        reviewRateTrend: 0,
        masteryRate: 0,
        masteryRateTrend: 0,
        rankRate: 0,
        rankRateTrend: 0,
        avgDifficulty: 0,
        avgDifficultyTrend: 0
      },
      months: [],
      tableColumns: [],
      tableData: [],
      monthlyData: [
        { 
          month: '场次', 
          type: 'gameCount',
          values: [],
          trends: []
        },
        { 
          month: '得分率', 
          type: 'scoreRate',
          values: [],
          trends: []
        },
        { 
          month: '订正率', 
          type: 'fixRate',
          values: [],
          trends: []
        },
        { 
          month: '复盘率', 
          type: 'reviewRate',
          values: [],
          trends: []
        },
        { 
          month: '排位率', 
          type: 'rankRate',
          values: [],
          trends: []
        },
        { 
          month: '掌握率', 
          type: 'masteryRate',
          values: [],
          trends: []
        },
        { 
          month: '平均难度', 
          type: 'avgDifficulty',
          values: [],
          trends: []
        }
      ],
      chart: null
    }
  },
  mounted() {
    this.fetchData()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
    if (this.chart) {
      this.chart.dispose()
    }
  },
  methods: {
    fetchData() {
      this.loading = true
      
      // 默认获取最近一年的数据
      request({
        url: '/api/analysis/monthly/comparison',
        method: 'get',
        params: {
          includeAll: false // 初始获取最近一年数据
        }
      }).then(response => {
        if (response.code === 200) {
          const data = response.data
          
          // 设置月份标签
          this.months = data.months.map(month => {
            const parts = month.match(/(\d{4})年(\d{2})月/)
            if (parts && parts.length === 3) {
              return `${parts[1]}/${parts[2]}`
            }
            return month
          })
          
          // 设置月度数据
          this.monthlyData = [
            { 
              month: '场次', 
              type: 'gameCount',
              values: data.gameCount.values || [],
              trends: data.gameCount.trends || []
            },
            { 
              month: '得分率', 
              type: 'scoreRate',
              values: data.scoreRate.values || [],
              trends: data.scoreRate.trends || []
            },
            { 
              month: '订正率', 
              type: 'fixRate',
              values: data.fixRate.values || [],
              trends: data.fixRate.trends || []
            },
            { 
              month: '复盘率', 
              type: 'reviewRate',
              values: data.reviewRate.values || [],
              trends: data.reviewRate.trends || []
            },
            { 
              month: '排位率', 
              type: 'rankRate',
              values: data.rankRate.values || [],
              trends: data.rankRate.trends || []
            },
            { 
              month: '掌握率', 
              type: 'masteryRate',
              values: data.masteryRate.values || [],
              trends: data.masteryRate.trends || []
            },
            { 
              month: '平均难度', 
              type: 'avgDifficulty',
              values: data.avgDifficulty.values || [],
              trends: data.avgDifficulty.trends || []
            }
          ]
          
          // 更新当前月份标签
          this.currentMonthLabel = this.months.length > 0 ? 
            this.months[0] : 
            this.getCurrentMonthLabel()
            
          // 设置最新月份的数据
          this.latestStats = data.latestStats || {}
          
          // 处理表格数据
          this.processTableData()
          
          // 初始化图表
          this.initChart()
        } else {
          this.$message.error(response.message || '获取数据失败')
        }
      }).catch(error => {
        console.error('获取月度环比数据失败:', error)
        this.$message.error('获取数据失败，请稍后重试')
      }).finally(() => {
        this.loading = false
      })
    },
    handleTimeRangeChange() {
      const isAllData = this.timeRange === 'all';
      
      // 显示加载状态
      this.loading = true;
      
      // 发送请求获取对应时间范围的数据
      request({
        url: '/api/analysis/monthly/comparison',
        method: 'get',
        params: {
          includeAll: isAllData,
          _t: new Date().getTime() // 保留时间戳避免缓存
        }
      }).then(response => {
        if (response.code === 200) {
          const data = response.data;
          
          // 设置月份标签
          this.months = data.months.map(month => {
            const parts = month.match(/(\d{4})年(\d{2})月/);
            if (parts && parts.length === 3) {
              return `${parts[1]}/${parts[2]}`;
            }
            return month;
          });
          
          // 更新月度数据
          this.monthlyData = [
            { 
              month: '场次', 
              type: 'gameCount',
              values: data.gameCount.values || [],
              trends: data.gameCount.trends || []
            },
            { 
              month: '得分率', 
              type: 'scoreRate',
              values: data.scoreRate.values || [],
              trends: data.scoreRate.trends || []
            },
            { 
              month: '订正率', 
              type: 'fixRate',
              values: data.fixRate.values || [],
              trends: data.fixRate.trends || []
            },
            { 
              month: '复盘率', 
              type: 'reviewRate',
              values: data.reviewRate.values || [],
              trends: data.reviewRate.trends || []
            },
            { 
              month: '排位率', 
              type: 'rankRate',
              values: data.rankRate.values || [],
              trends: data.rankRate.trends || []
            },
            { 
              month: '掌握率', 
              type: 'masteryRate',
              values: data.masteryRate.values || [],
              trends: data.masteryRate.trends || []
            },
            { 
              month: '平均难度', 
              type: 'avgDifficulty',
              values: data.avgDifficulty.values || [],
              trends: data.avgDifficulty.trends || []
            }
          ];
          
          // 更新当前月份标签
          this.currentMonthLabel = this.months.length > 0 ? 
            this.months[0] : 
            this.getCurrentMonthLabel();
            
          // 更新最新月份的数据
          this.latestStats = data.latestStats || {};
          
          // 处理表格数据
          this.processTableData();
          
          // 更新图表
          this.initChart();
        } else {
          this.$message.error(response.message || '获取数据失败');
        }
      }).catch(error => {
        console.error('获取月度环比数据失败:', error);
        this.$message.error('获取数据失败，请稍后重试');
      }).finally(() => {
        this.loading = false;
      });
    },
    processTableData() {
      // 过滤出有数据的月份
      const monthsWithData = [];
      const monthIndices = [];
      
      // 检查每个月份是否至少有一个指标有实际数据
      this.months.forEach((month, monthIndex) => {
        // 检查这个月是否至少有一个指标有真实数据
        const hasData = this.monthlyData.some(indicator => {
          const value = indicator.values[monthIndex];
          // 包含任何非零值的月份
          return value !== null && value !== undefined && 
                 (indicator.type === 'gameCount' ? value > 0 : value > 0.0001);
        });
        
        // 如果有真实数据，记录这个月份和它的索引
        if (hasData) {
          monthsWithData.push(month);
          monthIndices.push(monthIndex);
        }
      });
      
      // 按照时间顺序排序月份（确保最新月份在前面）
      const sortedIndices = [...monthIndices].sort((a, b) => {
        const monthA = this.months[a];
        const monthB = this.months[b];
        return this.compareMonths(monthB, monthA); // 反向比较，最新的在前
      });
      
      // 更新表格列，只使用有数据的月份，并按时间排序（最新的在前面）
      this.tableColumns = sortedIndices.map(index => this.months[index]);
      
      // 准备表格数据
      this.tableData = [
        { indicator: '场次', type: 'gameCount' },
        { indicator: '得分率', type: 'scoreRate' },
        { indicator: '订正率', type: 'fixRate' },
        { indicator: '复盘率', type: 'reviewRate' },
        { indicator: '排位率', type: 'rankRate' },
        { indicator: '掌握率', type: 'masteryRate' },
        { indicator: '平均难度', type: 'avgDifficulty' }
      ];
      
      // 填充表格数据并找出每行的最优值
      this.tableData.forEach(row => {
        const dataItem = this.monthlyData.find(item => item.type === row.type);
        if (dataItem) {
          // 找出每个指标的最优值索引（只考虑有数据的月份）
          let bestValueIndex = -1;
          
          // 对于不同指标，最优值的判定标准不同
          if (row.type === 'rankRate') {
            // 排位率是越小越好
            let minValue = Number.MAX_VALUE;
            sortedIndices.forEach((monthIndex, colIndex) => {
              const value = dataItem.values[monthIndex];
              if (value !== null && value !== undefined && value > 0.0001 && value < minValue) {
                minValue = value;
                bestValueIndex = colIndex;
              }
            });
          } else if (row.type === 'gameCount') {
            // 场次是越多越好
            let maxValue = -1;
            sortedIndices.forEach((monthIndex, colIndex) => {
              const value = dataItem.values[monthIndex];
              if (value !== null && value !== undefined && value > maxValue) {
                maxValue = value;
                bestValueIndex = colIndex;
              }
            });
          } else {
            // 其他指标都是越大越好
            let maxValue = -1;
            sortedIndices.forEach((monthIndex, colIndex) => {
              const value = dataItem.values[monthIndex];
              if (value !== null && value !== undefined && value > maxValue) {
                maxValue = value;
                bestValueIndex = colIndex;
              }
            });
          }
          
          // 为每个有数据的月份填充值和趋势
          sortedIndices.forEach((monthIndex, colIndex) => {
            const value = dataItem.values[monthIndex];
            if (value !== null && value !== undefined) {
              // 格式化显示值
              const formattedValue = row.type === 'gameCount' ? 
                (value || '-') : this.formatPercent(value);
              
              // 添加皇冠标识 - 修改皇冠位置和大小
              const isNonZeroValue = row.type === 'gameCount' ? value > 0 : value > 0.0001;
              if (colIndex === bestValueIndex && isNonZeroValue) {
                // 完全移除皇冠图标的所有样式设置，只对数值应用粗体样式
                row[`value${colIndex}`] = `👑<span style="font-weight: bold;">${formattedValue}</span>`;
              } else {
                row[`value${colIndex}`] = formattedValue;
              }
              
              // 处理趋势
              const trend = dataItem.trends[monthIndex];
              if (row.type === 'rankRate') {
                row[`trend${colIndex}`] = trend !== null && trend !== undefined ? -trend : null;
              } else {
                row[`trend${colIndex}`] = trend;
              }
            } else {
              // 如果单个指标没有数据则显示为'-'
              row[`value${colIndex}`] = '-';
              row[`trend${colIndex}`] = null;
            }
          });
        }
      });
    },
    getCurrentMonthLabel() {
      const now = new Date()
      const year = now.getFullYear()
      const month = now.getMonth() + 1
      return `${year}/${month < 10 ? '0' + month : month}`
    },
    initChart() {
      if (!this.$refs.chartContainer) return
      
      this.chart = echarts.init(this.$refs.chartContainer)
      this.updateChart()
    },
    updateChart() {
      if (!this.chart) return
      
      // 过滤出有数据的月份（与表格使用相同的过滤逻辑）
      const monthsWithData = [];
      const monthIndices = [];
      
      // 检查每个月份是否至少有一个指标有实际数据
      this.months.forEach((month, monthIndex) => {
        const hasData = this.monthlyData.some(indicator => {
          const value = indicator.values[monthIndex];
          return value !== null && value !== undefined && 
                 (indicator.type === 'gameCount' ? value > 0 : value > 0.0001);
        });
        
        if (hasData) {
          monthsWithData.push(month);
          monthIndices.push(monthIndex);
        }
      });
      
      // 按照时间顺序排序月份（与表格保持一致，最新月份在前）
      const sortedIndices = [...monthIndices].sort((a, b) => {
        const monthA = this.months[a];
        const monthB = this.months[b];
        return this.compareMonths(monthB, monthA); // 反向比较，最新的在前
      });
      
      // 准备图表数据（只包含有数据的月份）
      const filteredMonths = sortedIndices.map(index => this.months[index]);
      const series = [];
      const gameCountData = [];
      
      // 准备场次数据（柱状图）
      const gameCountItem = this.monthlyData.find(item => item.type === 'gameCount');
      sortedIndices.forEach((monthIndex, i) => {
        gameCountData[i] = gameCountItem ? gameCountItem.values[monthIndex] : 0;
      });
      
      // 指标配置：名称、颜色、使用的Y轴
      const metricConfigs = [
        { type: 'scoreRate', name: '得分率', color: '#1890FF', yAxisIndex: 0 },
        { type: 'fixRate', name: '订正率', color: '#2FC25B', yAxisIndex: 0 },
        { type: 'reviewRate', name: '复盘率', color: '#13C2C2', yAxisIndex: 0 },
        { type: 'masteryRate', name: '掌握率', color: '#FACC14', yAxisIndex: 0 },
        { type: 'avgDifficulty', name: '平均难度', color: '#722ED1', yAxisIndex: 0 },
        { type: 'rankRate', name: '排位率', color: '#FF4D4F', yAxisIndex: 1 }
      ];
      
      // 添加折线图系列
      metricConfigs.forEach(config => {
        const item = this.monthlyData.find(m => m.type === config.type);
        if (!item) return;
        
        const data = [];
        sortedIndices.forEach(monthIndex => {
          data.push(item.values[monthIndex]);
        });
        
        series.push({
          name: config.name,
          type: 'line',
          data: data,
          yAxisIndex: config.yAxisIndex,
          symbol: 'circle',
          symbolSize: 8,
          smooth: true,
          lineStyle: {
            width: 3,
            color: config.color
          },
          itemStyle: {
            color: config.color
          }
        });
      });
      
      // 最后添加场次柱状图
      series.push({
        name: '场次',
        type: 'bar',
        yAxisIndex: 2, // 使用第三个Y轴
        data: gameCountData,
        barWidth: 20,
        itemStyle: {
          color: '#FFA500'
        }
      });
      
      // 计算场次的合适刻度
      const maxGameCount = Math.max(...gameCountData.filter(v => v !== null && v !== undefined));
      const gameCountInterval = this.calculateAppropriateInterval(maxGameCount);
      
      // 设置图表选项
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            crossStyle: {
              color: '#999'
            }
          },
          formatter: function(params) {
            let result = params[0].axisValueLabel + '<br/>';
            
            params.forEach(param => {
              const marker = param.marker;
              const seriesName = param.seriesName;
              const value = param.value;
              
              if (seriesName === '场次') {
                result += marker + seriesName + ': ' + value + '<br/>';
              } else {
                // 所有百分比数据都保留两位小数
                result += marker + seriesName + ': ' + (value * 100).toFixed(2) + '%<br/>';
              }
            });
            
            return result;
          }
        },
        legend: {
          data: ['场次', '得分率', '订正率', '复盘率', '排位率', '掌握率', '平均难度'],
          bottom: 10
        },
        grid: {
          left: '3%', // 减少左侧空白区域
          right: '3%', // 减少右侧空白区域
          bottom: '15%',
          top: '7%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: filteredMonths, // 使用排序后的月份
          axisPointer: {
            type: 'shadow'
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '百分比',
            min: 0,
            max: 1,
            interval: 0.2,
            position: 'left',
            axisLabel: {
              formatter: function(value) {
                return (value * 100).toFixed(0) + '%';
              }
            },
            nameTextStyle: {
              align: 'left', // 文本左对齐
              padding: [0, 0, 0, -37] // 左侧负padding使文本更靠左
            },
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed',
                color: '#eee'
              }
            }
          },
          {
            type: 'value',
            name: '排位',
            min: 0,
            max: 1,
            interval: 0.2,
            position: 'left',
            offset: 40, // 调整偏移量，使两个轴更接近
            inverse: true, // 反转坐标轴
            axisLabel: {
              formatter: function(value) {
                return (value * 100).toFixed(0) + '%';
              }
            },
            nameLocation: 'start', // 将名称放在轴的起始位置
            nameGap: 5, // 减小名称与轴的距离
            nameTextStyle: {
              align: 'left', // 文本左对齐
              color: '#FF4D4F', // 排位率轴线颜色
              padding: [0, 0, 10, -30] // 左侧负padding使文本更靠左
            },
            splitLine: {
              show: false
            },
            axisLine: {
              lineStyle: {
                color: '#FF4D4F' // 排位率轴线颜色
              }
            }
          },
          {
            type: 'value',
            name: '场次',
            min: 0,
            max: maxGameCount + gameCountInterval,
            interval: gameCountInterval,
            position: 'right',
            axisLabel: {
              formatter: '{value}'
            },
            splitLine: {
              show: false
            },
            axisLine: {
              lineStyle: {
                color: '#FFA500' // 场次轴线颜色
              }
            },
            nameTextStyle: {
              color: '#FFA500', // 场次名称颜色
              padding: [0, 0, 0, 30] // 左侧负padding使文本更靠左
            }
          }
        ],
        series: series
      };
      
      this.chart.setOption(option, true); // 使用true强制刷新
    },
    resizeChart() {
      if (this.chart) {
        this.chart.resize()
      }
    },
    formatPercent(value) {
      if (value === undefined || value === null) return '-'
      return (value * 100).toFixed(2) + '%'
    },
    formatTrend(value) {
      if (value === undefined || value === null || value === '-') return '-'
      return (value >= 0 ? '+' : '') + value.toFixed(2) + '%'
    },
    formatTrendArrow(value) {
      if (value === undefined || value === null || value === '-') return '-'
      return value > 0 ? '↑ ' + value.toFixed(2) + '%' : 
             value < 0 ? '↓ ' + Math.abs(value).toFixed(2) + '%' : '-'
    },
    getTrendClass(value) {
      if (value === undefined || value === null || value === '-') return ''
      return value > 0 ? 'trend-up' : value < 0 ? 'trend-down' : ''
    },
    calculateAppropriateInterval(maxValue) {
      if (maxValue <= 0) return 1;
      
      // 找到合适的基数和倍数
      const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
      const normalized = maxValue / magnitude;
      
      // 根据规范化后的值选择合适的间隔
      let interval;
      if (normalized <= 1.5) interval = 0.2 * magnitude;
      else if (normalized <= 3) interval = 0.5 * magnitude;
      else if (normalized <= 7) interval = 1 * magnitude;
      else interval = 2 * magnitude;
      
      // 确保至少有5个刻度点
      if (maxValue / interval > 15) interval = interval * 2;
      if (maxValue / interval < 4) interval = interval / 2;
      
      return interval;
    },
    compareMonths(monthA, monthB) {
      // 假设月份格式为 YYYY/MM
      const [yearA, monthNumA] = monthA.split('/')
      const [yearB, monthNumB] = monthB.split('/')
      
      if (yearA !== yearB) {
        return parseInt(yearA) - parseInt(yearB)
      }
      return parseInt(monthNumA) - parseInt(monthNumB)
    },
    getDifficultyColorByPercentage(percentage) {
      if (percentage <= 14.29) {
        return '#F56C6C'; // 红题
      } else if (percentage <= 28.58) {
        return '#E6A23C'; // 橙题
      } else if (percentage <= 42.87) {
        return '#F7BA2A'; // 黄题
      } else if (percentage <= 57.15) {
        return '#67C23A'; // 绿题
      } else if (percentage <= 71.44) {
        return '#409EFF'; // 蓝题
      } else if (percentage <= 85.72) {
        return '#8E44AD'; // 紫题
      } else {
        return '#2C3E50'; // 黑题
      }
    },
    getDifficultyLevelByPercentage(percentage) {
      if (percentage <= 14.29) {
        return '红题难度';
      } else if (percentage <= 28.58) {
        return '橙题难度';
      } else if (percentage <= 42.87) {
        return '黄题难度';
      } else if (percentage <= 57.15) {
        return '绿题难度';
      } else if (percentage <= 71.44) {
        return '蓝题难度';
      } else if (percentage <= 85.72) {
        return '紫题难度';
      } else {
        return '黑题难度';
      }
    },
    extractNumericValue(value) {
      if (typeof value === 'number') {
        return value;
      }
      
      if (typeof value === 'string') {
        // 移除HTML标签
        const textOnly = value.replace(/<[^>]*>/g, '');
        // 提取百分号之前的数字
        const match = textOnly.match(/(\d+(\.\d+)?)/);
        return match ? parseFloat(match[1]) / 100 : 0;
      }
      
      return 0;
    },
    formatPercentWithoutHTML(value) {
      if (value === undefined || value === null) return '-';
      
      if (typeof value === 'number') {
        return (value * 100).toFixed(2) + '%';
      }
      
      if (typeof value === 'string') {
        // 移除HTML标签
        const textOnly = value.replace(/<[^>]*>/g, '');
        // 直接返回文本形式
        return textOnly;
      }
      
      return '-';
    }
  }
}
</script>

<style scoped>
.monthly-comparison {
  padding: 8px 0;
}

.page-title {
  font-size: 22px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 20px;
  text-align: center;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.data-card {
  margin-bottom: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.06);
  background-color: #fff;
}

.overview-card::deep(.el-card__body) {
  padding: 12px 20px;
}

.overview-content {
  padding: 6px 0;
}

.overview-row {
  margin-bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 20px;
}

.overview-col {
  flex: 1;
}

.stat-item {
  text-align: center;
  padding: 6px 0;
  border-radius: 6px;
  background: linear-gradient(135deg, #fdfbfb 0%, #f5f7ff 100%);
  border: 1px solid #edf0f7;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.stat-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 20px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 3px;
}

.stat-trend {
  font-size: 12px;
}

.trend-up {
  color: #67C23A;
}

.trend-down {
  color: #F56C6C;
}

.trend-indicator {
  font-size: 12px;
  margin-top: 5px;
}

.chart-container {
  height: 360px;
  width: 100%;
}

.el-table--striped .el-table__body tr.el-table__row--striped td {
  background: #fafafa;
}

.el-table--enable-row-hover .el-table__body tr:hover > td {
  background-color: #f5f7fa;
}

.el-table td, .el-table th {
  padding: 12px 0;
}

.el-table--border th, .el-table--border td {
  border-right: 1px solid #ebeef5;
}

.el-table::before {
  height: 1px;
  background-color: #ebeef5;
}

/* 确保表格的横线显示规律 */
.el-table--border::after, 
.el-table--group::after,
.el-table::before {
  background-color: #ebeef5;
}

.el-table__body tr:nth-child(even) > td {
  background-color: #fafafa;
}

.el-table__body tr:nth-child(odd) > td {
  background-color: #ffffff;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

.el-table {
  min-width: 100%;
}

.header-actions {
  display: flex;
  align-items: center;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

:deep(.difficulty-tooltip) {
  max-width: 350px !important;
  min-width: 320px !important;
  padding: 0 !important;
  border-radius: 6px !important;
}

:deep(.difficulty-tooltip-content) {
  padding: 15px;
}

:deep(.difficulty-description) {
  margin-bottom: 12px;
}

:deep(.difficulty-description p) {
  margin: 0 0 6px 0;
  line-height: 1.5;
  font-size: 13px;
  text-align: left;
}

:deep(.difficulty-levels) {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

:deep(.difficulty-level) {
  display: flex;
  align-items: center;
  text-align: left;
  font-size: 13px;
}

:deep(.level-color) {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  margin-right: 8px;
}

:deep(.level-range) {
  width: 130px;
  display: inline-block;
  font-weight: bold;
}

:deep(.level-name) {
  flex: 1;
}
</style> 

<style>
/* 兜底的全局样式，确保 tooltip（挂载到 body）中的色块样式生效 */
.difficulty-tooltip { max-width: 350px !important; min-width: 320px !important; padding: 0 !important; border-radius: 6px !important; }
.difficulty-tooltip-content { padding: 15px; }
.difficulty-description { margin-bottom: 12px; }
.difficulty-description p { margin: 0 0 6px 0; line-height: 1.5; font-size: 13px; text-align: left; }
.difficulty-levels { display: flex; flex-direction: column; gap: 8px; }
.difficulty-level { display: flex; align-items: center; text-align: left; font-size: 13px; }
.level-range { width: 130px; display: inline-block; font-weight: bold; }
.level-name { flex: 1; }
</style>